import { useState, useCallback } from 'react';
import { useNotification } from '../../../../contexts/NotificationContext';

// All permission categories & subsections
export const PERMISSION_STRUCTURE = [
  {
    category: 'DASHBOARD',
    subsections: [{ name: 'DASHBOARD', key: 'Dashboard', actions: ['View'] }]
  },
  {
    category: 'USER & ACCESS MANAGEMENT',
    subsections: [
      { name: 'USERS', key: 'Users', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'ROLES', key: 'Roles', actions: ['View', 'Add', 'Edit', 'Delete'] }
    ]
  },
  {
    category: 'PATIENT MANAGEMENT',
    subsections: [
      { name: 'PATIENTS', key: 'Patients', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'PATIENT SURGERY', key: 'Patients Surgery', actions: ['View'] },
      { name: 'PATIENT FEEDBACK', key: 'Patient Feedback', actions: ['View', 'Add', 'Edit', 'Delete'] }
    ]
  },
  {
    category: 'PRE-OPERATIVE',
    subsections: [
      { name: 'OT BOOKING', key: 'OT Booking', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'PRE-OP CHECKLIST', key: 'Pre-Op Checklist', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'PRE-OP ORDERS', key: 'Pre-Op Orders', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'PRE-ANAESTHETIC EVALUATION', key: 'Pre-Anaesthetic Evaluation', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'CONSENTS', key: 'Consents', actions: ['View', 'Add', 'Edit', 'Delete'] }
    ]
  },
  {
    category: 'INTRA-OPERATIVE',
    subsections: [
      { name: 'OPERATION SHEET', key: 'Operation Sheet', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'NURSING RECORD', key: 'Nursing Record', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'SURGICAL SAFETY', key: 'Surgical Safety Checklist', actions: ['View', 'Add', 'Edit', 'Delete'] }
    ]
  },
  {
    category: 'POST-OPERATIVE',
    subsections: [
      { name: 'POST-OP ORDERS', key: 'Post-Op Orders', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'POST-OP HANDOVER', key: 'Post-Op Handover', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'ALDERETE SCORE', key: 'Alderete Score', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'POST-OP INSTRUCTIONS', key: 'Post-Op Instructions', actions: ['View', 'Add', 'Edit', 'Delete'] }
    ]
  },
  {
    category: 'INVENTORY & SETTINGS',
    subsections: [
      { name: 'INVENTORY', key: 'Inventory', actions: ['View', 'Add', 'Edit', 'Delete'] },
      { name: 'OT ROOMS', key: 'OT Rooms', actions: ['View', 'Add', 'Edit', 'Delete'] }
    ]
  }
];

export const FLAT_PERMISSIONS = PERMISSION_STRUCTURE.flatMap(cat =>
  cat.subsections.flatMap(sub => sub.actions.map(a => `${sub.key} ${a}`))
);

const INITIAL_STATE = { name: '', description: '', permissions: [] };

export const useRoleForm = (initialData = INITIAL_STATE) => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState(initialData);

  const setInitialData = useCallback((data) => setFormData(data), []);

  const handleNameChange = useCallback((e) => {
    const newName = e.target.value;
    setFormData(prev => {
      const updated = { ...prev, name: newName };
      // If user types 'Admin', automatically select all permissions as a helpful default
      if (newName.toLowerCase() === 'admin' && prev.permissions.length === 0) {
        updated.permissions = [...FLAT_PERMISSIONS];
      }
      return updated;
    });
  }, []);

  const handleDescriptionChange = useCallback((e) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, description: val }));
  }, []);

  const togglePermission = useCallback((permKey) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter(p => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  }, []);

  const handleQuickAction = useCallback((actionType) => {
    setFormData(prev => {
      let newPerms = [...prev.permissions];
      if (actionType === 'Check All') {
        newPerms = [...FLAT_PERMISSIONS];
      } else if (actionType === 'Uncheck All') {
        newPerms = [];
      } else {
        const allOfSuffix = FLAT_PERMISSIONS.filter(p => p.endsWith(actionType));
        const allSelected = allOfSuffix.every(p => prev.permissions.includes(p));
        if (allSelected) {
          newPerms = newPerms.filter(p => !p.endsWith(actionType));
        } else {
          allOfSuffix.forEach(p => { if (!newPerms.includes(p)) newPerms.push(p); });
        }
      }
      return { ...prev, permissions: newPerms };
    });
  }, []);

  const validate = useCallback(() => {
    if (!formData.name.trim()) {
      showNotification('error', 'Role name cannot be empty.');
      return false;
    }
    if (formData.name.trim().length < 3) {
      showNotification('error', 'Role name must be at least 3 characters.');
      return false;
    }
    return true;
  }, [formData.name, showNotification]);

  const buildPayload = useCallback(() => ({
    name: formData.name.trim(),
    description: formData.description.trim(),
    permissions: formData.permissions,
  }), [formData]);

  return {
    formData,
    setInitialData,
    handleNameChange,
    handleDescriptionChange,
    togglePermission,
    handleQuickAction,
    validate,
    buildPayload,
  };
};
